import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { Loader } from '@/components/shared/Loader';
import {
	SentryDeploymentContext,
	SentryTag,
	UserMilestones,
} from '@/config/constants';
import { onboardingUrl } from '@/config/urls';
import {
	GetMyUserQuery,
	User as GraphQlUser,
	UpdateMyUserMutation,
	UpdateMyUserMutationVariables,
} from '@/graphql';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import {
	clearProviderCaches,
	PROVIDER_CACHE_KEYS,
	readProviderCache,
	removeProviderCache,
	writeProviderCache,
} from '@/helpers/providerCache';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { gql, request, ClientError } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { isNil } from 'ramda';
import { useNavigate } from 'react-router';

export type User = GraphQlUser;

type LogoutOptions = {
	noSnackbar?: boolean;
	redirectUrl?: string;
};

interface AuthContext {
	isLoading: boolean;
	isAuthenticated: boolean;
	isCopilot: boolean;
	token: string;
	user: User;
	updateUser: (input: UpdateMyUserMutationVariables) => Promise<User>;
	authenticate: (jwt: string, isCopilot?: boolean) => void;
	logout: (logoutOptions?: LogoutOptions) => void;
	hasMilestones: (milestones: UserMilestones[]) => boolean;
	appendMilestone: (milestone: UserMilestones) => Promise<User>;
}

const getMyUserQuery = gql`
	query getMyUser {
		getMyUser {
			id
			displayName
			email
			notificationId
			lastSeenAt
			gender
			birthYear
			color
			role
			skills
			completedMilestones
			notificationPreferences {
				chat
				videoChat
				videoChatActiveOnly
				groupChat
				reEngagementNudge
			}
		}
	}
`;

const updateMyUserMutation = gql`
	mutation updateMyUser($input: UserInput!) {
		updateMyUser(input: $input) {
			id
			displayName
			email
			notificationId
			lastSeenAt
			gender
			birthYear
			color
			role
			skills
			completedMilestones
			notificationPreferences {
				chat
				videoChat
				videoChatActiveOnly
				groupChat
				reEngagementNudge
			}
		}
	}
`;

const defaultUserValue: User = {
	email: '',
	id: '',
	displayName: '',
	skills: [],
	lastSeenAt: new Date().toISOString(),
	completedMilestones: [],
	notificationPreferences: {
		chat: false,
		groupChat: false,
		videoChat: false,
		videoChatActiveOnly: false,
		reEngagementNudge: false,
	},
};

const defaultValue: AuthContext = {
	isLoading: false,
	isAuthenticated: true,
	isCopilot: false,
	token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjEsInJvbGVzIjpbImRldmVsb3BlciIsInRlYW1BZG1pbiJdLCJ0ZWFtSWQiOjEsImlhdCI6MTY4ODUxMjU5MCwiZXhwIjoxNjg5MTE3MzkwfQ.sKbZhCOG8GwJOfPpIp7xYg_a9sl_drSHnmCduVnpWh0',
	user: defaultUserValue,
	updateUser: () => Promise.resolve(defaultUserValue),
	authenticate: () => {},
	logout: () => {},
	hasMilestones: () => false,
	appendMilestone: () => Promise.resolve(defaultUserValue),
};

const AuthContext = createContext<AuthContext>(defaultValue);

export function useAuth(): AuthContext {
	const context = useContext(AuthContext);
	if (isNil(context)) {
		throw new Error(
			`useEnvironment must be used within an EnvironmentProvider`,
		);
	}

	return context;
}

type AuthProviderProps = {
	children: (data: AuthContext) => ReactNode | null;
};

/* -------------------------------------------------------------------------- */
/*  Persistence helpers                                                        */
/*                                                                            */
/*  A single cached session is persisted across reloads under the provider     */
/*  cache. It holds everything needed to restore the session: the user, the    */
/*  JWT used to fetch it, and the copilot flag. On reload we render from this   */
/*  cache instantly and fetch a fresh user in the background using its JWT.     */
/* -------------------------------------------------------------------------- */

type CachedSession = {
	token: string;
	isCopilot: boolean;
	user: User;
};

const readCachedSession = (): CachedSession | null => {
	const session = readProviderCache<CachedSession>(PROVIDER_CACHE_KEYS.user);
	if (!session?.token || !session.user?.id) {
		return null;
	}
	return session;
};

const writeCachedSession = (session: CachedSession) =>
	writeProviderCache(PROVIDER_CACHE_KEYS.user, session);

const removeCachedSession = () => removeProviderCache(PROVIDER_CACHE_KEYS.user);

/*
 * Backwards compatibility: older versions stored the JWT (and copilot flag)
 * on its own under `asaphly-user`, with no cached user. When we find one of
 * these we treat it as "no cache" and fetch a fresh user using its JWT, then
 * migrate to the new cached session.
 */
const LEGACY_SESSION_STORAGE_KEY = 'asaphly-user';

type LegacySession = {
	token: string;
	isCopilot: boolean;
};

const readLegacySession = (): LegacySession | null => {
	const raw = window.localStorage.getItem(LEGACY_SESSION_STORAGE_KEY);
	if (!raw) {
		return null;
	}

	try {
		const parsed = JSON.parse(raw);
		if (parsed && typeof parsed === 'object' && parsed.token) {
			return { token: parsed.token, isCopilot: !!parsed.isCopilot };
		}
	} catch {
		// Even older sessions stored the raw JWT string instead of JSON.
	}

	return { token: raw, isCopilot: false };
};

const clearLegacySession = () =>
	window.localStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);

const fetchMyUser = async (apiUrl: string, jwt: string): Promise<User> => {
	const data = await request<GetMyUserQuery>(
		`${apiUrl}/graphql`,
		getMyUserQuery,
		{},
		{ Authorization: `Bearer ${jwt}` },
	);

	if (!data?.getMyUser) {
		throw new Error('User could not be authorized');
	}

	return data.getMyUser;
};

const isAuthError = (err: unknown): boolean =>
	err instanceof ClientError &&
	(err.response.status === 401 || err.response.status === 403);

/* -------------------------------------------------------------------------- */
/*  Auth state                                                                 */
/* -------------------------------------------------------------------------- */

type AuthState = {
	user: User;
	token: string;
	isCopilot: boolean;
	isAuthenticated: boolean;
	isLoading: boolean;
};

const unauthenticatedState: AuthState = {
	user: defaultUserValue,
	token: '',
	isCopilot: false,
	isAuthenticated: false,
	isLoading: false,
};

const getInitialState = (): AuthState => {
	const session = readCachedSession();
	if (!session) {
		return unauthenticatedState;
	}

	// We have a cached session: render immediately and revalidate in the
	// background using the JWT stored alongside the user.
	return {
		user: session.user,
		token: session.token,
		isCopilot: session.isCopilot,
		isAuthenticated: true,
		isLoading: false,
	};
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const {
		api: { url },
	} = useEnvironment();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();

	const [state, setState] = useState<AuthState>(getInitialState);

	const handleRedirects = useCallback(
		(userData: User) => {
			if (isOnboarding(userData)) {
				navigate(
					`${onboardingUrl}${window.location.search}`,
				);
			}
		},
		[navigate],
	);

	const clearSession = useCallback(() => {
		removeCachedSession();
		setState(unauthenticatedState);
	}, []);

	/**
	 * Fetches the current user for a JWT and, on success, persists + activates
	 * the session.
	 *
	 * `silent` is used when we already have a usable (cached) session on screen
	 * and are only revalidating in the background:
	 *   - no loader is shown
	 *   - a transient (network) failure keeps the cached session intact
	 *   - an auth failure (401/403) still logs the user out, since the token is
	 *     no longer valid
	 */
	const loadUser = useCallback(
		async (jwt: string, isCopilot: boolean, silent: boolean) => {
			if (!silent) {
				setState((prev) => ({ ...prev, isLoading: true }));
			}

			try {
				const user = await fetchMyUser(url, jwt);

				writeCachedSession({ token: jwt, isCopilot, user });
				setState({
					user,
					token: jwt,
					isCopilot,
					isAuthenticated: true,
					isLoading: false,
				});
				handleRedirects(user);
			} catch (err) {
				console.error(err);

				// Keep the cached session on a transient background failure.
				if (silent && !isAuthError(err)) {
					return;
				}

				clearSession();

				if (!silent) {
					enqueueSnackbar(
						'There was an error with loading your user',
						{ variant: 'warning' },
					);
				}
			}
		},
		[url, handleRedirects, clearSession, enqueueSnackbar],
	);

	const authenticate = useCallback(
		(jwt: string, isCopilot = false) => {
			void loadUser(jwt, isCopilot, false);
		},
		[loadUser],
	);

	const updateUser = useCallback(
		async (input: UpdateMyUserMutationVariables) => {
			try {
				const data = await request<
					UpdateMyUserMutation,
					UpdateMyUserMutationVariables
				>(`${url}/graphql`, updateMyUserMutation, input, {
					Authorization: `Bearer ${state.token}`,
				});

				if (!data?.updateMyUser) {
					throw new Error('User could not be updated');
				}

				writeCachedSession({
					token: state.token,
					isCopilot: state.isCopilot,
					user: data.updateMyUser,
				});
				setState((prev) => ({ ...prev, user: data.updateMyUser }));
				handleRedirects(data.updateMyUser);
				return data.updateMyUser;
			} catch (err) {
				console.error(err);
				enqueueSnackbar('There was an error with updating your user', {
					variant: 'warning',
				});
				throw new Error('Something went wrong');
			}
		},
		[state.token, state.isCopilot, url, enqueueSnackbar, handleRedirects],
	);

	const appendMilestone = useCallback(
		(milestone: UserMilestones) =>
			updateUser({
				input: {
					completedMilestones: [
						...state.user.completedMilestones,
						milestone,
					],
				},
			}),
		[updateUser, state.user.completedMilestones],
	);

	const logout = useCallback(
		(logoutOptions?: LogoutOptions) => {
			clearProviderCaches();
			queryClient.clear();
			setState(unauthenticatedState);

			if (logoutOptions?.noSnackbar) {
				enqueueSnackbar('You have successfully logged out', {
					variant: 'success',
				});
			}
			if (logoutOptions?.redirectUrl) {
				navigate(logoutOptions.redirectUrl);
			}
		},
		[enqueueSnackbar, navigate, queryClient],
	);

	const hasMilestones = useCallback(
		(milestones: UserMilestones[]) =>
			milestones.every((milestone) =>
				state.user.completedMilestones.includes(milestone),
			),
		[state.user.completedMilestones],
	);

	// Bootstrap the session exactly once on mount. `url` is resolved
	// synchronously by the EnvironmentProvider, so `loadUser` is stable from
	// the first render.
	const hasBootstrapped = useRef(false);
	useEffect(() => {
		if (hasBootstrapped.current) {
			return;
		}
		hasBootstrapped.current = true;

		const session = readCachedSession();
		if (session) {
			// New-format cache: already rendered (see `getInitialState`), drop
			// any stale legacy token and revalidate silently in the background.
			clearLegacySession();
			void loadUser(session.token, session.isCopilot, true);
			return;
		}

		const legacySession = readLegacySession();
		if (legacySession) {
			// Old-format token with no cached user: fetch the user (with the
			// loader) and migrate to the new cached session on success.
			void loadUser(
				legacySession.token,
				legacySession.isCopilot,
				false,
			).finally(clearLegacySession);
		}
	}, [loadUser]);

	useEffect(() => {
		Sentry.setUser(
			state.user.id
				? { id: state.user.id, email: state.user.email }
				: null,
		);
		Sentry.setTag(SentryTag.DEPLOYMENT, SentryDeploymentContext.EXTENSION);
	}, [state.user]);

	const value: AuthContext = useMemo(
		() => ({
			user: state.user,
			token: state.token,
			isLoading: state.isLoading,
			isAuthenticated: state.isAuthenticated,
			isCopilot: state.isCopilot,
			authenticate,
			logout,
			updateUser,
			hasMilestones,
			appendMilestone,
		}),
		[
			state,
			authenticate,
			logout,
			updateUser,
			hasMilestones,
			appendMilestone,
		],
	);

	if (state.isLoading) {
		return <Loader text="user" />;
	}

	return (
		<AuthContext.Provider value={value}>
			{children(value)}
		</AuthContext.Provider>
	);
};

export default AuthProvider;

export const isOnboarding = (user: User) =>
	!user.completedMilestones.includes(UserMilestones.ONBOARDING_COMPLETED);
